"use client";

import React, { useMemo } from "react";
import {
  BOOST_FROM_PAGE,
  boostService,
  useFeaturedStaysInfiniteQuery,
  type ISponsoredListing,
  type ISponsoredListingsResponse,
} from "@/src/shared/features/boost";
import type { ICampaignListing } from "@/src/shared/types/listing";
import ListingsGrid from "@/src/shared/components/listing/ListingsGrid";
import { homepageService, type TRailMatch } from "../services";
import { FROM_PAGE_ATTRIBUTE } from "@/src/shared/utils/navigationHistory";

interface IRailContentProps {
  match: TRailMatch;
  /** Featured Stays' first page from the server (the rail's page renders it for SEO). */
  initialFeatured?: ISponsoredListingsResponse;
  scrollRoot?: HTMLElement | null;
}

/** Every boosted stay of the tab, paged; each card carries its boost to the listing page. */
const FeaturedGrid: React.FC<
  Omit<IRailContentProps, "match"> & { tabSlug: string }
> = ({ tabSlug, initialFeatured, scrollRoot }) => {
  const campaign = boostService.campaignSlugForTab(tabSlug);
  const query = useMemo(
    () => (campaign ? boostService.homeQuery(campaign, new Date()) : null),
    [campaign],
  );
  const { data, isPending, hasNextPage, isFetchingNextPage, fetchNextPage } =
    useFeaturedStaysInfiniteQuery(query, initialFeatured);

  const items = useMemo(
    () => data?.pages.flatMap((page) => page.data) ?? [],
    [data],
  );
  const byId = useMemo(
    () => new Map<number, ISponsoredListing>(items.map((i) => [i.id, i])),
    [items],
  );
  const cardQuery = (card: ICampaignListing) => {
    const item = byId.get(card.listing.id);
    return item
      ? boostService.linkQuery(item, BOOST_FROM_PAGE.home(tabSlug, true))
      : undefined;
  };

  return (
    <ListingsGrid
      listings={items.map(boostService.toCardListing)}
      sponsored
      cardQuery={cardQuery}
      isLoading={query !== null && isPending}
      hasMore={hasNextPage}
      isLoadingMore={isFetchingNextPage}
      onLoadMore={() => void fetchNextPage()}
      scrollRoot={scrollRoot}
    />
  );
};

/** What a rail's page and drawer list: the rail's cards, or the tab's Featured Stays. */
const RailContent: React.FC<IRailContentProps> = ({
  match,
  initialFeatured,
  scrollRoot,
}) =>
  match.kind === "featured" ? (
    <FeaturedGrid
      tabSlug={match.tab.slug}
      initialFeatured={initialFeatured}
      scrollRoot={scrollRoot}
    />
  ) : (
    <div
      {...{
        [FROM_PAGE_ATTRIBUTE]: homepageService.railFromPage(
          match.tab,
          match.rail,
        ),
      }}
    >
      <ListingsGrid
        listings={match.rail.listings.map(homepageService.railCardToListing)}
      />
    </div>
  );

export default RailContent;
